import React, { ComponentType, ReactNode, useMemo } from 'react';

import PropTypes from 'prop-types';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

import { PRODUCT_IDS } from './consts';

export type ProductIdsType = typeof PRODUCT_IDS;
export type ProductIdType = ProductIdsType[keyof ProductIdsType];

type CurrentProductIdContextType = {
  productId?: ProductIdType;
};

/**
 * Context for current product ID.
 */
const CurrentProductIdContext =
  getOrCreateGlobalContext<CurrentProductIdContextType>(
    CONTEXT_NAMES.CurrentProductIdContext,
  );

const useCurrentProductIdContext = () =>
  useContextWithValidation(CurrentProductIdContext, {
    hook: 'useCurrentProductIdContext',
    provider: 'CurrentProductIdProvider',
  });

export const useCurrentProductId = () => {
  const currentProductIdContext = useCurrentProductIdContext();
  return currentProductIdContext?.productId;
};

type CurrentProductIdProviderProps = {
  productId?: ProductIdType;
  children: ReactNode;
};

export const CurrentProductIdProvider: ComponentType<
  CurrentProductIdProviderProps
> = ({ children, productId }) => {
  const value = useMemo(() => {
    return { productId };
  }, [productId]);
  return (
    <CurrentProductIdContext.Provider value={value}>
      {children}
    </CurrentProductIdContext.Provider>
  );
};

CurrentProductIdProvider.displayName = 'CurrentProductIdProvider';

CurrentProductIdProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node,
  /** The product ID */
  productId: PropTypes.oneOf(Object.values(PRODUCT_IDS)),
};
